import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import styles from "./Overview.module.css";
import { useData } from "@/lib/api/api";
import { creatorOverview } from "@/lib/api/creator.fragments";
import { getCreatorPortrait } from "@/components/creator/utils";
import Text from "@/components/base/text";
import Title from "@/components/base/title";
import Translate from "@/components/base/translate/Translate";
import CreatorImage from "@/components/creator/CreatorImage";
import Icon from "@/components/base/icon";
import AiMarkdown from "@/components/base/markdown/AiMarkdown";
import { IconLink } from "@/components/base/iconlink/IconLink";
import animations from "@/components/base/animation/animations.module.css";
import ExternalSvg from "@/public/icons/external_small.svg";
import ArrowDownSvg from "@/public/icons/arrow_down_line.svg";
import AwardSvg from "@/public/icons/award.svg";
import Link from "@/components/base/link";
import Tooltip from "@/components/base/tooltip";

export function useCreatorOverview(creatorId) {
  const { data, isLoading } = useData(creatorOverview({ display: creatorId }));

  const portrait = getCreatorPortrait(data?.creatorByDisplay);

  const image = portrait && {
    url: portrait.src,
    alt: portrait.alt,
    attributionText: portrait.credits,
  };

  return {
    data: data && {
      ...(data?.creatorByDisplay || {}),
      image,
    },
    isLoading: isLoading,
  };
}
/**
 * Displays the creator overview with breadcrumbs, name, occupation,
 * awards, generated summary and image.
 */
export function Overview({
  className = "",
  creatorId,
  creatorData,
  isLoading,
}) {
  const summaryText =
    creatorData?.generated?.summary?.text ||
    creatorData?.generated?.dataSummary?.text;
  const forfatterwebUrl = creatorData?.forfatterweb?.url;
  return (
    <section className={`${styles.background} ${className}`}>
      <Container fluid>
        <Row className={styles.overview}>
          <Col xs={12} xl={3} className={styles.breadcrumbs}>
            <Text type="text3" tag="p" lines={1}>
              {Translate({ context: "creator", label: "creator-breadcrumb" })} /{" "}
              {creatorId}
            </Text>
          </Col>
          <Col
            xs={{ span: 12, order: 2 }}
            md={{ span: 7, order: 1 }}
            xl={{ span: 5, order: 1 }}
          >
            <Title type="title2" tag="h1" lines={1}>
              {creatorData?.display || creatorId}
            </Title>
            <Text
              type="text3"
              tag="p"
              className={styles.occupation}
              lines={1}
              skeleton={isLoading && !creatorData?.wikidata?.occupation}
            >
              {creatorData?.wikidata?.occupation?.join(", ") || ""}
            </Text>
            {isLoading && !summaryText && (
              <Text
                type="text3"
                tag="p"
                className={styles.occupation}
                lines={4}
                skeleton={isLoading}
              >
                ...
              </Text>
            )}
            {creatorData?.wikidata?.awards?.length > 0 && (
              <div className={styles.awards}>
                <Icon size={{ w: 20, h: 20 }} alt="">
                  <AwardSvg />
                </Icon>
                <Text type="text3" tag="p" className={styles.award} lines={2}>
                  {Translate({
                    context: "creator",
                    label: "selected-prizes",
                  })}
                  : {creatorData?.wikidata?.awards?.join(", ")}
                </Text>
              </div>
            )}
            {summaryText && (
              <div className={styles.summary}>
                <AiMarkdown
                  creatorId={creatorId}
                  text={summaryText}
                  urlTransform={(href) => `/materiale/title/${href}`}
                  fadeIn={false}
                  // disclaimer={tmpDisclaimer}
                />
              </div>
            )}
            {!isLoading && creatorId && (
              <>
                <div className={styles.disclaimer}>
                  <Tooltip
                    labelToTranslate="tooltip-disclaimer"
                    placement="bottom-start"
                  >
                    <Text
                      type="text5"
                      tag="div"
                      className={styles.disclaimerLabel}
                    >
                      {Translate({
                        context: "tooltip",
                        label: "tooltip-disclaimer-label",
                      })}
                    </Text>
                  </Tooltip>
                </div>

                <div className={`${styles.links} `}>
                  <IconLink
                    className={styles.arrowDownLink}
                    href={"#"}
                    iconPlacement="right"
                    iconSrc={ArrowDownSvg}
                    iconAnimation={[
                      animations["h-elastic"],
                      animations["f-elastic"],
                    ]}
                    textType="text3"
                    target="_blank"
                    onClick={(e) => {
                      e.preventDefault();
                      const publications = document.getElementById(
                        "creator-publications"
                      );
                      if (publications) {
                        publications.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    {" "}
                    <Text type="text3" tag="span">
                      {Translate({
                        context: "creator",
                        label: "view-publications",
                      })}
                    </Text>
                  </IconLink>
                  <Link
                    border={{ bottom: { keepVisible: true } }}
                    href={"/find?q.all=" + encodeURIComponent(`"${creatorId}"`)}
                  >
                    <Text type="text3" tag="span">
                      {Translate({
                        context: "creator",
                        label: "creator-search",
                      })}
                    </Text>
                  </Link>
                  {forfatterwebUrl && (
                    <IconLink
                      className={styles.externalLink}
                      href={forfatterwebUrl}
                      iconPlacement="right"
                      iconSrc={ExternalSvg}
                      iconAnimation={[
                        animations["h-elastic"],
                        animations["f-elastic"],
                      ]}
                      textType="text3"
                      target="_blank"
                      iconStyle={{ stroke: "blue" }}
                    >
                      {Translate({
                        context: "creator",
                        label: "creator-forfatterweb",
                      })}
                    </IconLink>
                  )}
                </div>
              </>
            )}
          </Col>
          <Col
            xs={{ span: 12, order: 1 }}
            md={{ span: 5, order: 2 }}
            xl={{ span: 4, order: 2 }}
            className={styles.imageCol}
          >
            {(isLoading || creatorData?.image?.url) && (
              <>
                <CreatorImage
                  src={creatorData?.image?.url}
                  alt={creatorData?.image?.alt || creatorData?.display}
                  skeleton={isLoading && !creatorData?.image?.url}
                />
                {creatorData?.image?.attributionText && (
                  <Text
                    type="text5"
                    tag="p"
                    className={styles.attribution}
                    lines={2}
                    clamp
                  >
                    {creatorData?.image?.attributionText}
                  </Text>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default function Wrap({ creatorId }) {
  const { data: creatorData, isLoading: isCreatorLoading } =
    useCreatorOverview(creatorId);

  return (
    <Overview
      creatorId={creatorId}
      creatorData={creatorData}
      isLoading={isCreatorLoading}
    />
  );
}
